/**
 * EAI-1486 — admin impersonation ("act as user") client helpers.
 *
 * Impersonation is carried per-request by the `X-Impersonate-User` header
 * (injected in the Apollo authMiddleware from `sessionStorage.impersonateUserId`).
 * Starting/stopping just flips that sessionStorage key and does a full page
 * load so every layer — React Context (ME query), Redux, Apollo cache —
 * re-hydrates cleanly under the resolved identity.
 *
 * The impersonated session opens in a NEW TAB so the admin keeps their own
 * admin-dashboard tab intact. Because the flag lives in sessionStorage (per
 * tab, and only copied to a child tab by some browsers), the target is handed
 * to the new tab through a short-lived localStorage payload plus a matching
 * one-time token in the new tab's URL — `consumeImpersonationHandoff()` (wired
 * into `onClientEntry` in gatsby-browser.js) moves it into that tab's own
 * sessionStorage before any query runs. The token pairing is what keeps an
 * unrelated tab that happens to reload from picking the payload up.
 *
 * The admin keeps their own Cognito JWT throughout; the backend only honors
 * the header when the caller's real token is a platform admin, so nothing
 * here grants privilege on its own.
 */

import { purgePersistedState } from "../redux/store";

const IMPERSONATE_KEY = "impersonateUserId";
// Kept purely for the banner UI so it can name the impersonated user without
// an extra round-trip. Not trusted for any auth decision.
const IMPERSONATE_LABEL_KEY = "impersonateUserLabel";
// Cross-tab handoff (localStorage) + the query param that authorizes reading it.
const HANDOFF_KEY = "pendingImpersonation";
const HANDOFF_PARAM = "impersonateHandoff";
// The new tab boots within a second or two; anything older is stale and gets
// dropped rather than applied to whatever tab happens to load next.
const HANDOFF_TTL_MS = 60 * 1000;

const createHandoffToken = () => {
  const cryptoObj = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint32Array(4);
    cryptoObj.getRandomValues(buf);
    return Array.from(buf, n => n.toString(16)).join("");
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
};

export const getImpersonatedUserId = () => {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(IMPERSONATE_KEY);
};

export const getImpersonatedUserLabel = () => {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(IMPERSONATE_LABEL_KEY);
};

export const isImpersonating = () => Boolean(getImpersonatedUserId());

/**
 * Reserve the tab the impersonated session will open in.
 *
 * MUST be called synchronously from the click handler: the caller validates
 * the target with a server round-trip first, and a `window.open()` issued
 * after that await is no longer tied to the user gesture, so browsers block
 * it as a popup.
 *
 * @returns {Window|null} the blank tab, or null if the browser blocked it
 */
export const openImpersonationTab = () => {
  if (typeof window === "undefined") return null;
  return window.open("", "_blank");
};

/**
 * Begin impersonating a target user in a new tab, leaving the admin's current
 * tab as-is. Writes the one-shot handoff payload (target id + display label
 * for the banner) and points the reserved tab at the app root with the
 * matching token; that tab picks the payload up on boot (see
 * `consumeImpersonationHandoff`).
 *
 * @param {number|string} targetUserId
 * @param {string} [label] human-readable name/email for the banner
 * @param {Window|null} [tab] tab reserved by `openImpersonationTab()`
 * @returns {boolean} false when no tab could be opened (popup blocked)
 */
export const startImpersonation = (targetUserId, label = "", tab = null) => {
  if (typeof window === "undefined" || !targetUserId) return false;
  const token = createHandoffToken();
  try {
    localStorage.setItem(
      HANDOFF_KEY,
      JSON.stringify({ userId: String(targetUserId), label, token, ts: Date.now() })
    );
  } catch (e) {
    return false;
  }
  const target = `${window.location.origin}/?${HANDOFF_PARAM}=${encodeURIComponent(token)}`;
  if (tab && !tab.closed) {
    tab.location.href = target;
    return true;
  }
  // No reserved tab (caller didn't pre-open one, or it was closed) — try
  // directly; this is the path most likely to hit a popup blocker.
  const opened = window.open(target, "_blank");
  if (!opened) {
    localStorage.removeItem(HANDOFF_KEY);
    return false;
  }
  return true;
};

/**
 * New-tab side of the handoff. Runs on cold load (gatsby-browser
 * `onClientEntry`) before React/Apollo boot, so the very first ME query
 * already carries `X-Impersonate-User`.
 *
 * Consumes the payload only when this tab's URL carries the matching token.
 * Removing the token from the URL is deliberately NOT done here — see
 * `clearImpersonationHandoffParam`.
 *
 * @returns {boolean} true when this tab entered an impersonated session
 */
export const consumeImpersonationHandoff = () => {
  if (typeof window === "undefined") return false;
  const token = new URL(window.location.href).searchParams.get(HANDOFF_PARAM);
  if (!token) return false;

  let payload = null;
  try {
    const raw = localStorage.getItem(HANDOFF_KEY);
    localStorage.removeItem(HANDOFF_KEY);
    payload = raw ? JSON.parse(raw) : null;
  } catch (e) {
    return false;
  }
  if (!payload?.userId || payload.token !== token) return false;
  if (!payload.ts || Date.now() - payload.ts > HANDOFF_TTL_MS) return false;

  sessionStorage.setItem(IMPERSONATE_KEY, String(payload.userId));
  if (payload.label) sessionStorage.setItem(IMPERSONATE_LABEL_KEY, payload.label);
  // Drop any session location inherited from the admin's tab; the impersonated
  // user resolves their own s_locations server-side from their assigned
  // locations.
  sessionStorage.removeItem("currentLocationId");
  return true;
};

/**
 * Strip the handoff token from the URL, so a refresh or a copied link can't
 * replay it (the payload is already single-use, this is belt-and-braces).
 *
 * MUST run after Gatsby's first render (`onInitialClientRender`), never in
 * `onClientEntry`: Gatsby keys the initial page resources by
 * `pathname + search`, so rewriting the URL before the first render makes
 * `EnsureResources` look up a key it never loaded and throw
 * "was not able to find resources for path" — a blank page.
 */
export const clearImpersonationHandoffParam = () => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(HANDOFF_PARAM)) return;
  url.searchParams.delete(HANDOFF_PARAM);
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
};

/**
 * End impersonation and return to the admin's own context.
 */
export const stopImpersonation = () => {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(IMPERSONATE_KEY);
  sessionStorage.removeItem(IMPERSONATE_LABEL_KEY);
  sessionStorage.removeItem("currentLocationId");
  // Same reason as startImpersonation, in reverse: don't carry the impersonated
  // user's filters or forced quick filter back into the admin's own context.
  purgePersistedState();
  window.location.href = "/";
};
